<template>
  <Tab
    :value="value"
    :disabled="disabled"
    :as="as"
    :as-child="asChild"
    :unstyled="true"
    :pt="passThrough"
    class="meridia-tab"
    v-bind="$attrs"
  >
    <div
      v-if="hasIcon"
      class="meridia-tab-content inline-flex items-center justify-center w-full gap-0"
    >
      <slot name="icon" v-if="$slots.icon" />
      <MeridiaIcon
        v-else-if="props.icon"
        :name="props.icon"
        :type="props.iconType"
        class="meridia-tab-icon mr-xs icon-s shrink-0 leading-none text-inherit"
      />
      <span class="meridia-tab-label"><slot /></span>
    </div>
    <slot v-else />
  </Tab>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import Tab from 'primevue/tab';
import { usePassThrough } from 'primevue/passthrough';
import type { PassThrough } from '@primevue/core';
import type { TabPassThroughOptions } from 'primevue/tab';
import type { MeridiaTabProps } from 'src/design_system/components/tab/types';
import MeridiaIcon from 'src/design_system/components/icon/MeridiaIcon.vue';

const props = withDefaults(
  defineProps<MeridiaTabProps>(),
  {
    disabled: false,
    asChild: false,
    iconType: 'material-symbols-outlined',
  }
);

const slots = useSlots();
const hasIcon = computed(() => !!props.icon || !!slots.icon);

const passThrough = computed(() =>
  usePassThrough(
    props.pt,
    {
      root: ({ context }: { context: { active: boolean } }) => ({
        class: [
          'meridia-tab-root inline-flex items-center justify-center gap-2 cursor-pointer select-none whitespace-nowrap transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-primary-border-focus flex-1 min-w-0 h-full px-0 w-full text-medium uppercase font-bold mr-[1px] last:mr-0 text-slate-800',
          context?.active
            ? 'meridia-tab-root--active bg-white hover:bg-white'
            : 'meridia-tab-root--inactive bg-slate-200 hover:bg-slate-300',
        ],
      }),
    } as TabPassThroughOptions,
    { mergeSections: false, mergeProps: true }
  ) as PassThrough<TabPassThroughOptions>
);
</script>

<style lang="scss" scoped>
.meridia-tab-icon {
  font-size: 16px;
  line-height: 1;
  color: inherit;
}
</style>
